import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { OeAppService } from './oe-app.service';
import { GamificationActivityType } from './types/gamification-activity-type';
import { Observable, of } from 'rxjs';
import { GamificationPointsDto } from './types/GamificationPointsDto';
import { catchError, map, share } from 'rxjs/operators';
import { GamificationLeagueDto } from './types/GamificationLeagueDto';
import { GamificationProfileDto } from './types/GamificationProfileDto';
import { GamificationLeaderboardDto } from './types/GamificationLeaderboardDto';
import { GamificationBadgeDto } from './types/GamificationBadgeDto';
import { GamificationPersonBadgeDto } from './types/GamificationPersonBadgeDto';
import { Store } from '@ngrx/store';
import { AppState } from './reducers/index.tx';
import { getGamificationProfileAction } from './actions/gamification.actions';
import { GamificationSettingsDto } from './types/GamificationSettingsDto';

@Injectable({
  providedIn: 'root',
})
export class GamificationService {
  intervalRefresh;

  constructor(
    private http: HttpClient,
    private oeAppService: OeAppService,
    private store: Store<AppState>
  ) { }

  startRefreshGamification() {
    this.stopRefreshGamification();

    this.intervalRefresh = setInterval(() => {
      // Refresh the leaderboards data after 10 minutes
      this.store.dispatch(getGamificationProfileAction());
    }, 600000);
  }

  stopRefreshGamification() {
    if (this.intervalRefresh) {
      clearInterval(this.intervalRefresh);
    }
  }

  getActivityPoints(
    activityType?: GamificationActivityType
  ): Observable<GamificationPointsDto[]> {
    return this.http
      .get<GamificationPointsDto[]>(
        `${this.oeAppService.gamificationServiceHost}/v1/activity-points` +
        (activityType ? `?activityType=${activityType}` : '')
      )
      .pipe(catchError(() => of([])));
  }

  getActivityPointsMap() {
    return this.getActivityPoints().pipe(
      map(
        (data: GamificationPointsDto[]) =>
          new Map<GamificationActivityType, number>(
            data.map((e: GamificationPointsDto) => [e.activityType, e.points] as [GamificationActivityType, number])
          )
      ),
      share()
    );
  }

  getGameProfile(contactId: string): Observable<GamificationProfileDto> {
    return this.http
      .get<GamificationProfileDto>(
        `${this.oeAppService.gamificationServiceHost}/v1/game-profiles?contactId=${contactId}`
      )
      .pipe(
        catchError((err: HttpErrorResponse) =>
          of(err.status === 404 ? null : undefined)
        )
      );
  }

  getLeagues(): Observable<GamificationLeagueDto[]> {
    return this.http
      .get<GamificationLeagueDto[]>(
        `${this.oeAppService.gamificationServiceHost}/v1/leagues`
      )
      .pipe(catchError(() => of([])));
  }

  getLeaderboard(contactId: string): Observable<GamificationLeaderboardDto> {
    return this.http
      .get<GamificationLeaderboardDto>(
        `${this.oeAppService.gamificationServiceHost}/v1/leader-boards?contactId=${contactId}`
      )
      .pipe(
        catchError((err: HttpErrorResponse) =>
          of(err.status === 404 ? null : undefined)
        )
      );
  }

  getActivityBadges(
    activityType: GamificationActivityType,
    forDate?: string
  ): Observable<GamificationBadgeDto[]> {
    return this.http
      .get<GamificationBadgeDto[]>(
        `${this.oeAppService.gamificationServiceHost}/v1/activity-badges?activityType=${activityType}${forDate ? '&forDate=' + forDate : ''
        }`
      )
      .pipe(catchError(() => of([])));
  }

  getPersonBadges(
    contactId: string,
    badgeUuids: string[] = undefined
  ): Observable<GamificationPersonBadgeDto[]> {
    const badgeQuery =
      badgeUuids && badgeUuids.length
        ? badgeUuids.map((e) => `&badgeUuids=${e}`).join('')
        : '';

    return this.http
      .get<GamificationPersonBadgeDto[]>(
        `${this.oeAppService.gamificationServiceHost}/v1/person-badges?contactId=${contactId}${badgeQuery}`
      )
      .pipe(catchError(() => of([])));
  }

  getSettings(): Observable<GamificationSettingsDto> {
    return this.http.get<GamificationSettingsDto>(
      `${this.oeAppService.gamificationServiceHost}/v1/settings`
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/gamification.service.ts